import { motion } from "framer-motion";
import { fadeUp, staggerContainer, viewportOnce } from "@/animations/motion";
import { Check } from "@/icons";
import type { DetailFeature, DetailBenefit } from "@/types/detail";

/** Feature grid — each item explains the business benefit, not just the module. */
export function FeatureGrid({ features }: { features: DetailFeature[] }) {
  return (
    <motion.div
      variants={staggerContainer(0.06, 0.05)}
      initial="hidden"
      whileInView="visible"
      viewport={viewportOnce}
      className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3"
    >
      {features.map((f) => (
        <motion.div
          key={f.title}
          variants={fadeUp}
          className="group rounded-[1.25rem] border border-border bg-card p-6 transition-all duration-300 hover:-translate-y-1 hover:border-primary/20 hover:shadow-[0_20px_50px_-28px_rgba(15,118,110,0.3)]"
        >
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Check className="h-4 w-4" />
          </span>
          <h3 className="mt-4 font-heading text-base font-semibold text-foreground">
            {f.title}
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            {f.description}
          </p>
        </motion.div>
      ))}
    </motion.div>
  );
}

/** Benefit grid — outcome-oriented cards with icons. */
export function BenefitGrid({ benefits }: { benefits: DetailBenefit[] }) {
  return (
    <motion.div
      variants={staggerContainer(0.06, 0.05)}
      initial="hidden"
      whileInView="visible"
      viewport={viewportOnce}
      className="grid gap-5 sm:grid-cols-2"
    >
      {benefits.map(({ title, description, icon: Icon }) => (
        <motion.div
          key={title}
          variants={fadeUp}
          className="flex gap-4 rounded-[1.25rem] border border-border bg-card p-6"
        >
          <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent/10 text-accent">
            <Icon className="h-5 w-5" />
          </span>
          <div>
            <h3 className="font-heading text-base font-semibold text-foreground">
              {title}
            </h3>
            <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
              {description}
            </p>
          </div>
        </motion.div>
      ))}
    </motion.div>
  );
}
